import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme, Tooltip, Box } from '@mui/material';
import { ResponsiveContainer, Treemap } from 'recharts';
import { TreemapNode } from 'recharts/types/util/types';

import { DashboardQueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog';
import { Query } from 'system/query/Query';
import TopicsTooltip from 'components/widgets/charts/parts/TopicsTooltip';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import TreeMapContent from './TreeMapNode';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import { treeMapDefaultSettings } from './treeMapUtils';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName } from 'system/widget/Widget';

export interface Topic {
  appearances: number;
  cluster_id: number;
  count: number;
  distribution: number;
  velocity: number|undefined;
  est_total_appearances: number;
  est_total_count: number;
  est_total_negative_count: number;
  est_total_positive_count: number;
  label: string;
  language: string;
  negative_count: number;
  positive_count: number;
  positivity: number;
  query?: Query;
  relationships: any[];
  relevance: number;
  sentimental: number;
  entity?: object;
  timeline: Array<number>;
  timeline_distribution: Array<number>;
}

function TopicsTreeMapWidget(props: WithQueryWidgetProps): JSX.Element {
  const theme = useTheme();
  const COLORS = theme.colors.namedColors.chartColorArray;

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'topics-tree-map');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'topics-tree-map');
  const data = apiResponseInfo?.responseBody?.topics || [];

  const [hoveredNode, setHoveredNode] = useState<TreemapNode | null>(null);
  const [selectedNode, setSelectedNode] = useState<TreemapNode | null>(null);

  const currentState = {
    ...treeMapDefaultSettings,
    ...props.widget?.data,
    ...props.widget?.data?.settings
  };

  const [/* widgetHeight */, setWidgetHeight] = useState<number>(0);
  const titleWrapRef = useRef<HTMLDivElement | null | undefined>();

  useEffect(() => {
    if (currentState.widgetTitle !== 'none') {
      if (props.contentRef && titleWrapRef.current) {
        const height = props.contentRef.current.clientHeight - titleWrapRef.current.clientHeight;
        setWidgetHeight(height);
      }
    } else {
      setWidgetHeight(props.contentRef.current.clientHeight);
    }
  }, [props.contentRef.current.clientHeight, titleWrapRef.current]);

  const formattedData = useMemo(() => {
    const clusters: Record<string | number, { name: string, size: number, topic?: Topic }[]> = {};
    // Group topics by cluster
    data.forEach((topic: Topic) => {
      if (!clusters[topic.cluster_id]) {
        clusters[topic.cluster_id] = [];
      }
      clusters[topic.cluster_id].push({
        name: topic.label,
        size: topic.relevance ? topic.relevance : topic.count,
        topic: topic
      });

    });

    const treemapData: {
      name: string | undefined,
      children: {
        name: string,
        size: number,
        topic?: Topic
      }[]
    }[] = [];

    // Convert to treemap data format
    Object.keys(clusters).forEach((clusterId: string) => {
      const cluster = clusters[clusterId];
      treemapData.push({
        name: clusterId,
        children: cluster
      });
    });

    return treemapData;
  }, [data]);

  return (
    <WidgetLayout
      header={
        <WidgetTitle
          alignment={currentState.widgetTitleAlignment}
          customTitle={currentState.customWidgetTitle}
          defaultTitle={"Topics TreeMap"}
          settingsTitle={currentState.widgetTitle}
          style={{ pb: 2 }}
          widgetQueryTitle={apiResponseInfo?.queryDisplayTitle} />
      } >
      {data.length !== 0 &&
        <>
          <Tooltip
            title={hoveredNode ? <TopicsTooltip topic={hoveredNode.topic} /> : ''}
            open={Boolean(hoveredNode)}
            followCursor={true}
          >
            <Box sx={{
              height: "100%"
            }}>
              <ResponsiveContainer>
                <Treemap
                  isAnimationActive={false}
                  data={formattedData}
                  dataKey="size"
                  aspectRatio={16 / 9}
                  stroke="#fff"
                  fill="#8884d8"
                  animationDuration={400}
                  animationEasing="ease-out"
                  content={<TreeMapContent colors={COLORS} showClusterLabel={true} />}
                  onClick={(node: TreemapNode) => {
                    setHoveredNode(null);
                    setSelectedNode(node);
                  }}
                  onMouseEnter={(node: TreemapNode) => setHoveredNode(node)}
                  onMouseLeave={() => { setHoveredNode(null) }}
                />
              </ResponsiveContainer>
            </Box>
          </Tooltip>
          {selectedNode &&
            <DashboardQueryDrilldownDialog
              title={selectedNode.topic.label}
              queryInfo={props.queryInfo}
              onClose={() => setSelectedNode(null)}
              activeQueryIds={apiResponseInfo?.originatingRequest?.activeQueryIds}
              drilldownQuery={new Query({ query: selectedNode.topic.query })}
              widgetRequestInfo={props.widget?.request_info} />
          }
        </>
      }
      {data.length === 0 &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </WidgetLayout>
  );
}

export default withQueryWidget(TopicsTreeMapWidget, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
